

















































































import { computed, defineComponent, onBeforeUnmount, PropType, ref, watch } from "@vue/composition-api";
import { format } from "date-fns";
import { isMobile } from "@/lib/browserUtils";
import { MediaFilesController } from "@/lib/MediaFilesController";
import { PostFileData } from "@/types";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseButton from "@/components/BaseButton";
import BaseSpinner from "@/components/BaseSpinner";
import ModalView from "@/components/ModalView";
import {
  TRAILER_THUMB_WIDTH,
  TRAILER_THUMBNAILS_COUNT,
  useTrailerBlobThumbnails,
} from "../hooks/useTrailerBlobThumbnails";

const FIXED_TRAILER_DURATION_SEC = 120;
const MAX_VIDEO_RECOVERY_ATTEMPTS = 12;

type TrailerRange = { startTime: number; finishTime: number };

export default defineComponent({
  name: "MakeTrailerModal",
  components: { BaseIcon, BaseSpinner, ModalView, BaseButton },
  props: {
    show: { type: Boolean, required: true },
    videoFile: { type: Object as PropType<PostFileData | null>, default: null },
    selectedRange: { type: Object as PropType<TrailerRange | null>, default: null },
  },
  setup(props, { emit }) {
    const videoEl = ref<HTMLVideoElement | null>(null);
    const trackEl = ref<HTMLElement | null>(null);
    const durationSec = ref(0);
    const selectedStartSec = ref(0);
    const selectedEndSec = ref(FIXED_TRAILER_DURATION_SEC);
    const currentTimeSec = ref(0);
    const dragStartClientX = ref(0);
    const dragStartStartSec = ref(0);
    const hasVideoMetadata = ref(false);
    const videoSeeking = ref(false);
    const videoElementKey = ref(0);
    let dragTrackWidth = 0;

    let scheduledSeekFrame: number | null = null;
    let appliedPreviewSrc: string | null = null;
    let shouldResumeAfterSeek = false;
    let recoveringVideoElement = false;
    let videoRecoveryAttempts = 0;

    const {
      blobThumbnails,
      thumbnailLoading,
      cancelBlobFrameExtraction,
      extractBlobFrames,
      trySetVisibleVideoThumbnail,
    } = useTrailerBlobThumbnails({ videoEl, selectedStartSec });

    const videoUrl = computed(() => {
      if (!props.videoFile?.url) return "";
      if (props.videoFile.url.startsWith("blob")) return props.videoFile.url;
      return MediaFilesController.getVideoUrl(props.videoFile.url);
    });

    const timelineThumbnails = computed(() => {
      const fileUrl = props.videoFile?.url;
      if (!fileUrl || !durationSec.value) return [];

      if (videoUrl.value.startsWith("blob")) {
        return blobThumbnails.value;
      }

      const thumbnailSrc = MediaFilesController.getVideoThumbnail(fileUrl, TRAILER_THUMB_WIDTH);

      return Array.from({ length: TRAILER_THUMBNAILS_COUNT }, () => ({ src: thumbnailSrc }));
    });

    const selectedStartPercent = computed(() => {
      if (!durationSec.value) return 0;
      return (selectedStartSec.value / durationSec.value) * 100;
    });
    const selectedEndPercent = computed(() => {
      if (!durationSec.value) return 0;
      return (selectedEndSec.value / durationSec.value) * 100;
    });
    const currentTimePercent = computed(() => {
      const windowSec = selectedEndSec.value - selectedStartSec.value;
      if (!windowSec) return 0;
      const offsetSec = currentTimeSec.value - selectedStartSec.value;
      return (offsetSec / windowSec) * 100;
    });
    const selectedWidthPercent = computed(() => Math.max(0, selectedEndPercent.value - selectedStartPercent.value));
    const selectedRangeLabel = computed(() => {
      return `${format(selectedStartSec.value * 1000, "mm:ss")} - ${format(selectedEndSec.value * 1000, "mm:ss")}`;
    });

    const pauseVideo = () => {
      if (!videoEl.value) return;
      videoEl.value.pause();
    };

    const getClientX = (event: MouseEvent | TouchEvent) => {
      if ("touches" in event) {
        return event.touches[0]?.clientX ?? event.changedTouches[0]?.clientX ?? 0;
      }
      return event.clientX;
    };

    const seekToStart = () => {
      if (!videoEl.value) return;
      videoSeeking.value = true;
      videoEl.value.currentTime = selectedStartSec.value;
      currentTimeSec.value = selectedStartSec.value;
    };

    const cancelScheduledSeek = () => {
      if (scheduledSeekFrame === null) return;
      window.cancelAnimationFrame(scheduledSeekFrame);
      scheduledSeekFrame = null;
    };

    const scheduleSeekToStart = () => {
      cancelScheduledSeek();
      scheduledSeekFrame = window.requestAnimationFrame(() => {
        scheduledSeekFrame = null;
        seekToStart();
      });
    };

    const setFixedRange = () => {
      const maxStart = Math.max(0, durationSec.value - FIXED_TRAILER_DURATION_SEC);
      const selectedStartTimeSec = props.selectedRange ? Math.floor(props.selectedRange.startTime / 1000) : 0;
      const start = Math.min(Math.max(0, selectedStartTimeSec), maxStart);

      selectedStartSec.value = start;
      selectedEndSec.value = Math.min(durationSec.value, start + FIXED_TRAILER_DURATION_SEC);
    };

    const onDragMove = (event: MouseEvent | TouchEvent) => {
      if (!dragTrackWidth || !durationSec.value) return;
      if ("touches" in event) event.preventDefault();

      const currentDuration = selectedEndSec.value - selectedStartSec.value;
      const dx = getClientX(event) - dragStartClientX.value;
      const deltaSec = (dx / dragTrackWidth) * durationSec.value;
      const maxStart = Math.max(0, durationSec.value - currentDuration);
      const nextStart = Math.min(Math.max(dragStartStartSec.value + deltaSec, 0), maxStart);

      selectedStartSec.value = nextStart;
      selectedEndSec.value = Math.min(durationSec.value, nextStart + currentDuration);

      pauseVideo();
      if (!isMobile()) {
        scheduleSeekToStart();
      }
    };

    const onDragEnd = () => {
      cancelScheduledSeek();
      window.removeEventListener("mousemove", onDragMove);
      window.removeEventListener("mouseup", onDragEnd);
      window.removeEventListener("touchmove", onDragMove);
      window.removeEventListener("touchend", onDragEnd);
      seekToStart();
    };

    const onDragStart = (event: MouseEvent | TouchEvent) => {
      event.preventDefault();
      dragTrackWidth = trackEl.value?.getBoundingClientRect().width ?? 0;
      dragStartClientX.value = getClientX(event);
      dragStartStartSec.value = selectedStartSec.value;

      window.addEventListener("mousemove", onDragMove);
      window.addEventListener("mouseup", onDragEnd);
      window.addEventListener("touchmove", onDragMove, { passive: false });
      window.addEventListener("touchend", onDragEnd);
    };

    const onClose = () => {
      pauseVideo();
      cancelScheduledSeek();
      emit("close");
    };

    const onVideoMetadataLoaded = () => {
      const currentDuration = videoEl.value?.duration ?? 0;
      durationSec.value = Number.isFinite(currentDuration) ? currentDuration : 0;
      hasVideoMetadata.value = durationSec.value > 0;
      if (recoveringVideoElement) {
        recoveringVideoElement = false;
        seekToStart();
        return;
      }
      videoRecoveryAttempts = 0;
      setFixedRange();
      seekToStart();
      void trySetVisibleVideoThumbnail(videoUrl.value.startsWith("blob"));
      if (videoUrl.value.startsWith("blob") && durationSec.value > 0 && videoEl.value && !blobThumbnails.value.length) {
        void extractBlobFrames(
          videoUrl.value,
          durationSec.value,
          videoEl.value.videoWidth,
          videoEl.value.videoHeight,
          isMobile() ? 1 : TRAILER_THUMBNAILS_COUNT
        );
      }
    };

    const onVideoTimeUpdate = () => {
      const video = videoEl.value;
      if (!video) return;
      if (video.currentTime >= selectedEndSec.value) {
        video.pause();
        video.currentTime = selectedEndSec.value;
        currentTimeSec.value = selectedEndSec.value;
        return;
      }
      currentTimeSec.value = video.currentTime;
    };

    const onVideoSeeking = () => {
      const video = videoEl.value;
      if (!video) return;
      videoSeeking.value = true;
      if (video.currentTime < selectedStartSec.value) {
        video.currentTime = selectedStartSec.value;
      } else if (video.currentTime > selectedEndSec.value) {
        video.currentTime = selectedEndSec.value;
      }
      currentTimeSec.value = video.currentTime;
    };

    const onVideoSeeked = () => {
      videoSeeking.value = false;
      currentTimeSec.value = videoEl.value?.currentTime ?? currentTimeSec.value;
      void trySetVisibleVideoThumbnail(videoUrl.value.startsWith("blob"));
      if (!shouldResumeAfterSeek) return;
      shouldResumeAfterSeek = false;
      videoEl.value?.play().catch(() => undefined);
    };

    const onVideoPlay = () => {
      if (!isMobile() || !videoSeeking.value) return;
      shouldResumeAfterSeek = true;
      pauseVideo();
    };

    const onVideoError = () => {
      if (hasVideoMetadata.value) {
        pauseVideo();
        videoSeeking.value = false;
        shouldResumeAfterSeek = false;
        videoRecoveryAttempts += 1;
        if (videoRecoveryAttempts > MAX_VIDEO_RECOVERY_ATTEMPTS) {
          return;
        }
        recoveringVideoElement = true;
        videoElementKey.value += 1;
        return;
      }
      videoRecoveryAttempts = 0;
      durationSec.value = 0;
      hasVideoMetadata.value = false;
      selectedStartSec.value = 0;
      selectedEndSec.value = FIXED_TRAILER_DURATION_SEC;
      currentTimeSec.value = 0;
      cancelBlobFrameExtraction();
    };

    const onApply = () => {
      const payload: { startTime: number; finishTime: number; previewSrc?: string } = {
        startTime: Math.floor(selectedStartSec.value * 1000),
        finishTime: Math.floor(selectedEndSec.value * 1000),
      };
      if (blobThumbnails.value.length && durationSec.value > 0) {
        const index = Math.min(
          Math.round((selectedStartSec.value / durationSec.value) * TRAILER_THUMBNAILS_COUNT),
          TRAILER_THUMBNAILS_COUNT - 1
        );
        payload.previewSrc = blobThumbnails.value[index]?.src;
        appliedPreviewSrc = payload.previewSrc ?? null;
      }
      emit("apply", payload);
      onClose();
    };

    watch(
      () => props.show,
      value => {
        if (!value) {
          pauseVideo();
          cancelScheduledSeek();
          cancelBlobFrameExtraction(appliedPreviewSrc);
          appliedPreviewSrc = null;
          return;
        }
        seekToStart();
      }
    );

    watch(
      () => props.selectedRange,
      () => {
        if (!props.show || !durationSec.value) return;
        setFixedRange();
        seekToStart();
      }
    );

    onBeforeUnmount(() => {
      onDragEnd();
      pauseVideo();
      cancelScheduledSeek();
      cancelBlobFrameExtraction(appliedPreviewSrc);
    });

    return {
      videoEl,
      videoElementKey,
      trackEl,
      videoUrl,
      durationSec,
      timelineThumbnails,
      thumbnailLoading,
      selectedStartSec,
      selectedEndSec,
      selectedRangeLabel,
      selectedStartPercent,
      selectedEndPercent,
      currentTimePercent,
      selectedWidthPercent,
      onDragStart,
      onClose,
      onApply,
      onVideoMetadataLoaded,
      onVideoTimeUpdate,
      onVideoSeeking,
      onVideoSeeked,
      onVideoPlay,
      onVideoError,
    };
  },
});
